import { ChangeDetectorRef, EventEmitter, inject, Injectable, Input, Output } from '@angular/core';
import { OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import { Account, AccountColor } from 'app/core/models';
import { PanelDef } from 'app/shared/components/panels/panels.types';
import { calendarColors } from 'app/modules/calendar/sidebar/calendar-colors';
import { AccountState } from '@state/account/state';
import { Observable } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { RelatedToType } from '@shared/enums';
import { NewPermission } from '@core/enums/permission';
import { UserState } from '@state/user/state';

export interface PanelsInterfaceComponent {
  project: any;
  account: any;
  panelDef: any;
  onUpdate: any;
  data: any;
  onEditPanelDef: any;
  // Optional. Used only to silent update tasks panel last selected taskList.
  silentPanelDefUpdated?: EventEmitter<PanelDef<any>>;
  readonly?: boolean;
  relatedToType?: RelatedToType;
  canEditPanelDef: boolean;
  reloadAllPanelsOfTypes?: EventEmitter<string[]>; // string is the list of panels that should be refreshed
}

@Injectable()
export abstract class PanelsInterfaceComponentBase extends OnDestroyMixin implements PanelsInterfaceComponent {
  @Input() project: any;
  @Input() model?: any;
  @Input() account: Account;
  @Input() panelDef: PanelDef<any>;
  @Input() data: any;
  @Input() readonly: boolean = false;
  @Output() onUpdate: EventEmitter<string> = new EventEmitter();
  @Output() onEditPanelDef: EventEmitter<any> = new EventEmitter();
  @Output() silentPanelDefUpdated: EventEmitter<any> = new EventEmitter();
  @Output() panelDefUpdated: EventEmitter<any> = new EventEmitter();
  @Output() reloadAllPanelsOfTypes: EventEmitter<string[]> = new EventEmitter();

  panelName: string;
  panelColor: any = null;
  editMode: boolean;
  changeTitleMode = false;
  titleFieldDef: any;
  canEditPanelDef = true;
  headerColors: any;
  protected store = inject(Store);

  @Select(AccountState.selectAccountColorsWithNone) protected readonly accountColors$: Observable<AccountColor[]>;

  constructor(protected readonly changeDetectorRef: ChangeDetectorRef) {
    super();
    this.headerColors = ['None', ...calendarColors];
  }

  saveLayout(): void {
    if (this.panelName) {
      this.panelDef.title = this.panelName;
      this.titleFieldDef.label = this.panelName;
    }

    if (this.panelColor) {
      this.panelDef.color = this.panelColor;
    } else {
      this.panelDef.color = null;
    }

    if (this.model) {
      this.panelDefUpdated.emit(this.panelDef);
    } else if (this.project) {
      this.onEditPanelDef.emit(this.panelDef);
    }

    this.toggleEditMode(false);
  }

  toggleEditMode(editMode: boolean | null = null): void {
    if (editMode === null) {
      this.editMode = !this.editMode;
    } else {
      this.editMode = editMode;
    }

    if (this.editMode === false) {
      this.panelName = null;
      if (this.model) {
        this.model[this.titleFieldDef.name] = null;
      } else if (this.project) {
        this.project[this.titleFieldDef.name] = null;
      }
    }

    this.changeTitleMode = false;
    this.changeDetectorRef.markForCheck();
  }

  toggleChangeTitleMode(): void {
    this.changeTitleMode = true;
  }

  update(params: any): void {
    let currentKey = Object.keys(params)[0];
    if (currentKey === this.titleFieldDef.name) {
      this.panelName = params[this.titleFieldDef.name];
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  selectHeaderColor(color: string): void {
    if (color?.toLowerCase() === 'none') {
      this.panelColor = null;
    } else {
      this.panelColor = color;
    }

    this.saveLayout();
  }

  canEditDeal(): boolean {
    return (
      this.data?.project?.permissions?.is_editable &&
      this.store?.selectSnapshot(UserState.hasPermission(NewPermission.DEALS_EDIT))
    );
  }
}
